<template>
  <q-page padding>
    <parents-categories @goNext="goNext"></parents-categories>
    <q-page-sticky
      position="bottom-right"
      :offset="[16, 24]"
      v-if="hasRecipients"
    >
      <q-btn round color="negative" icon="warning" @click="initALert" />
    </q-page-sticky>
    <!-- Alert -->
    <q-dialog maximized v-model="alertModalOpened">
      <q-layout class="alert-modal">
        <div class="row text-center">
          <div class="col-12 absolute-top alert-header">
            <h4>{{ $t('common.sendAlert') }}</h4>
          </div>
          <div class="col-12 absolute-center">
            <q-btn round color="negative" size="50px">
              <div class="big-alt">{{ secondsRemaining }}</div>
            </q-btn>
          </div>
          <div class="col-12 absolute-bottom" :class="{ 'alert-bottom--android': $q.platform.is.android, 'alert-bottom--ios': $q.platform.is.ios }">
            <q-btn
              size="22px"
              icon="clear"
              outline
              round
              @click="dismissAlert"
            />
            <div class="alert-label">{{ $t('common.cancel') }}</div>
          </div>
        </div>
      </q-layout>
    </q-dialog>
  </q-page>
</template>

<style lang="stylus">

.alert-modal {
  background: rgba(0, 0, 0, 0.85);
}

.alert-header {
  color: white;
  padding-top: 25px;
  text-align: center;
}

.alert-bottom--ios {
  color: white;
  padding-bottom: 75px;
  text-align: center;
}

.alert-bottom--android {
  color: white;
  padding-bottom: 25px;
  text-align: center;
}

.alert-label {
  padding-top: 5px;
  font-size: 16px;
}

.big-alt {
  font-size: 50px;
}
</style>

<script>
import ParentsCategories from 'src/components/categories/Parents'
import { mapGetters } from 'vuex'
import { scroll } from 'quasar'
import eventBus from 'src/eventBus'
import { getCurrentPosition } from 'src/utils/getCurrentPosition'
import {
  buildAlertDetailsDialog,
  formatLonlat,
  parseLonlat,
  withTimeout
} from 'src/utils/alertPosition'

const { setScrollPosition } = scroll

const ALERT_COUNTDOWN = 5
// NOTE: FEAT-4328 — le fix GPS est demandé pendant le compte à rebours ; au
// pire l'alerte part ALERT_POSITION_GRACE ms après lui, avec le cache
const ALERT_POSITION_TIMEOUT = 4500
const ALERT_POSITION_MAX_AGE = 60000
const ALERT_POSITION_GRACE = 1000

export default {
  components: { ParentsCategories },
  created() {
    eventBus.$on('newPosition', (newPosition) => {
      this.position = newPosition
    })
  },
  data() {
    return {
      alertModalOpened: false,
      secondsRemaining: 5,
      intervalHandle: null,
      alertPositionRequest: null,
      position: {
        longitude: null,
        latitude: null
      },
      alertError: {
        title: this.$t('notification.categories.alertError.title'),
        message: this.$t('notification.categories.alertError.message')
      },
      location: null
    }
  },
  computed: {
    ...mapGetters({
      reporter: 'reporter/attributes'
    }),
    hasRecipients() {
      return (
        this.reporter &&
        'recipients' in this.reporter &&
        Array.isArray(this.reporter.recipients) &&
        this.reporter.recipients.length
      )
    },
    lonlat() {
      return formatLonlat(this.position)
    }
  },
  methods: {
    getGeolocation() {
      const latestKnownPosition = parseLonlat(
        sessionStorage.getItem('latestKnownPosition')
      )
      this.position = latestKnownPosition || {
        longitude: null,
        latitude: null
      }
    },
    requestAlertPosition() {
      if (!this.$q.platform.is.capacitor) {
        return Promise.resolve(null)
      }
      return getCurrentPosition(this.$q.dialog, {
        timeout: ALERT_POSITION_TIMEOUT,
        maximumAge: ALERT_POSITION_MAX_AGE
      }).then((freshPosition) => {
        const lonlat = formatLonlat(freshPosition)
        if (lonlat) {
          this.position = freshPosition
          try {
            sessionStorage.setItem('latestKnownPosition', lonlat)
          } catch (error) {
            // the fresh position is still used for this alert
          }
        }
        return freshPosition
      })
    },
    goNext(categoryId) {
      this.$router.push({
        path: `/categories/${categoryId}/childs`,
        query: {
          location: this.location
        }
      })
    },
    async initALert() {
      this.alertModalOpened = true
      window.clearInterval(this.intervalHandle)
      this.alertPositionRequest = this.requestAlertPosition()
      this.countdown(ALERT_COUNTDOWN).then(() => this.sendAlert())
    },
    initAlertDetails({ withoutPosition = false } = {}) {
      this.$q
        .dialog(
          buildAlertDetailsDialog((key) => this.$t(key), { withoutPosition })
        )
        .onOk((data) => this.sendAlertDetails(data))
        .onCancel(() =>
          setScrollPosition(window, window.document.body.scrollHeight)
        )
    },
    dismissAlert() {
      this.alertModalOpened = false
      this.alertPositionRequest = null
      clearInterval(this.intervalHandle)
    },
    async sendAlert() {
      const alertPositionRequest = this.alertPositionRequest
      this.dismissAlert()
      const freshPosition = await withTimeout(
        alertPositionRequest,
        ALERT_POSITION_GRACE
      )
      const lonlat = formatLonlat(freshPosition) || this.lonlat
      this.$store.dispatch('reporter/sendAlert', { lonlat, message: '' }).then(
        () => this.initAlertDetails({ withoutPosition: !lonlat }),
        () => this.$q.dialog(this.alertError)
      )
    },
    sendAlertDetails(message) {
      this.$store.dispatch('reporter/sendAlert', { lonlat: '', message }).then(
        () => setScrollPosition(window, window.document.body.scrollHeight),
        () => {
          this.$q.dialog(this.alertError)
          setScrollPosition(window, window.document.body.scrollHeight)
        }
      )
    },
    countdown(duration) {
      return new Promise((resolve) => {
        // how many seconds
        this.secondsRemaining = duration

        // every second, call the "tick" function
        // have to make it into a variable so that you can stop the interval later!!!
        this.intervalHandle = setInterval(() => {
          // stop is down to zero
          if (this.secondsRemaining === 1) {
            resolve()
          }
          // subtract from seconds remaining
          this.secondsRemaining--
        }, 1000)
      })
    }
  },
  mounted() {
    this.getGeolocation()
    if (this.$route.query.location) {
      /**
       * If we need to get base-category from the query
       * do this this.$route.query.baseCategory
       */
      this.location = this.$route.query.location
    }
  },
  beforeUnmount() {
    eventBus.$off('newPosition')
  }
}
</script>
